import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { doc, updateDoc, setDoc, deleteDoc, writeBatch, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Objective1,
  MilestoneMap1,
  MapInvitation,
  UserWithAccessAndRole,
  UserProfile1,
  SignUpLink1,
  SharedMapsUpdate,
  Result1,
} from '../types';
import type { Human } from '@mm-shared/types/Human';
import type { HumanRelationship } from '@mm-shared/types/HumanRelationship';
import {
  generateRelationshipDocId,
  hasOverlappingDateRange,
} from '@mm-shared/types/HumanRelationship';
import {
  handleChange,
  fetchMyMilestoneMapsData,
  fetchSharedMilestoneMapsData,
} from './map3DataSlice';
import type { RootState } from '../types/store';
import { upsertItem } from '../utils/upsertItem';
import type { AppDispatch } from '../types/store';
import { getAuth, deleteUser } from 'firebase/auth';
import { buildHumanFromUserProfile } from '../utils/humanProfileUtils';
import { stripUndefinedFields } from '../utils/stripUndefinedFields';

// Generic update type
export type UpdateData<T> = {
  uuid: string;
  updates: T extends MilestoneMap1 ? Partial<T> | MapInvitation : Partial<T>;
};

export type OperationType =
  | 'map_create'
  | 'map_update'
  | 'map_delete'
  | 'objective_create'
  | 'objective_update'
  | 'objective_delete'
  | 'user_create'
  | 'user_update'
  | 'user_delete'
  | 'signup_link_update'
  | 'signup_link_create'
  | 'shared_maps_update'
  | 'human_create'
  | 'human_update'
  | 'human_delete'
  | 'human_relationship_create'
  | 'human_relationship_update'
  | 'human_relationship_delete';

// Individual operation type
interface PendingOperation {
  operationId: string;
  type: OperationType;
  timestamp: number; // When operation was first created
  lastAttempt?: number; // When operation was last attempted
  status: 'pending' | 'processing' | 'completed' | 'error';
  retryCount: number;
  data: UpdateData<Objective1 | MilestoneMap1 | UserProfile1 | SignUpLink1 | Human | HumanRelationship>;
  error?: string;
}

// Main state interface
interface PendingState {
  operations: PendingOperation[];
  syncStatus: 'idle' | 'syncing' | 'error';
  lastSyncError: string | null;
  lastSyncAttempt: number | null;
}

// Initial State
const initialState: PendingState = {
  operations: [],
  syncStatus: 'idle',
  lastSyncError: null,
  lastSyncAttempt: null,
};

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // ms
const BATCH_SIZE = 5;

// Helper function to dispatch both queue and change operations
const dispatchChanges = (
  dispatch: AppDispatch,
  type: OperationType,
  data: UpdateData<Objective1 | MilestoneMap1 | UserProfile1 | SignUpLink1 | Human | HumanRelationship>,
) => {
  const operationId = crypto.randomUUID();

  dispatch(
    // Queue write operation to be processed in batches by processOperationQueue.
    queueOperation({
      operationId,
      type,
      data,
    }),
  );

  dispatch(
    // Apply optimistic update to local state via map3DataSlice.handleChange.
    handleChange({
      operationId,
      type,
      data,
      source: 'map3PendingSlice',
    }),
  );

  return operationId;
};

/**
 * Updates a specific objective in Firestore and the local state.
 * @param payload - The update payload object
 * @param payload.objectiveUuid - The UUID of the objective to update
 * @param payload.updates - Partial objective properties to update. Only the specified properties will be modified
 *                         while preserving other existing values. Example: { title: "New Title" } will only
 *                         update the title field.
 * @returns A promise that resolves to the update payload containing the objectiveUuid and applied updates
 * @throws Error if the objective with the specified UUID is not found in Firestore
 */
export const updateObjective = createAsyncThunk<
  UpdateData<Objective1>,
  UpdateData<Objective1>,
  { dispatch: AppDispatch }
>('map3Pending/updateObjective', async (payload: UpdateData<Objective1>, { dispatch }) => {
  try {
    const { uuid, updates } = payload;
    dispatchChanges(dispatch, 'objective_update', { uuid, updates });
    return payload;
  } catch (error) {
    console.error('Error updating objective:', error);
    throw error;
  }
});

export const updateMilestoneMap = createAsyncThunk<
  UpdateData<MilestoneMap1>,
  UpdateData<MilestoneMap1>,
  { dispatch: AppDispatch; state: RootState }
>(
  'map3Pending/updateMilestoneMap',
  async (payload: UpdateData<MilestoneMap1>, { dispatch, getState }) => {
    try {
      const { uuid, updates } = payload;
      // Remove createdBy if it's undefined to avoid Firestore errors
      const { createdBy, ...rest } = updates;
      const sanitizedUpdates = {
        ...rest,
        ...(createdBy !== undefined && {
          createdBy: typeof createdBy === 'object' ? (createdBy as any).name : createdBy,
        }),
      };

      // Get current map state to check for user access changes
      const state = getState();
      const milestoneMap = state.map3Data.milestoneMaps.find((m: MilestoneMap1) => m.uuid === uuid);

      // Note: Ideally, user profiles and maps would be further decoupled.
      // See docs/someday_maybe/IMPLEMENT_HOOK_SYSTEM.md for working draft
      // future state vision.
      if (milestoneMap && 'usersWithAccess' in updates) {
        const currentUsers = milestoneMap.usersWithAccess || [];
        const updatedUsers = (updates.usersWithAccess as UserWithAccessAndRole[]) || [];

        // Find users that have been added (present in updatedUsers but not in currentUsers)
        const addedUsers = updatedUsers.filter(
          (updatedUser) => !currentUsers.some((currentUser) => currentUser.uid === updatedUser.uid),
        );

        // Find users that have been removed (present in currentUsers but not in updatedUsers)
        const removedUsers = currentUsers.filter(
          (currentUser) => !updatedUsers.some((updatedUser) => updatedUser.uid === currentUser.uid),
        );

        // Process added users - update their shared maps list
        for (const userWithAccess of addedUsers) {
          if (userWithAccess.uid && userWithAccess.uid.trim() !== '') {
            dispatch(
              queueSharedMapsUpdate({
                type: 'add',
                mapUuid: uuid,
                mapShortId: milestoneMap.shortId,
                mapTitle: milestoneMap.title || '',
                targetUserUid: userWithAccess.uid,
                mapOwnerName: milestoneMap.owner.name,
              }),
            );
          }
        }

        // Process removed users - update their shared maps list
        for (const userWithAccess of removedUsers) {
          if (userWithAccess.uid && userWithAccess.uid.trim() !== '') {
            dispatch(
              queueSharedMapsUpdate({
                type: 'remove',
                mapUuid: uuid,
                mapShortId: milestoneMap.shortId,
                mapTitle: milestoneMap.title || '',
                targetUserUid: userWithAccess.uid,
                mapOwnerName: milestoneMap.owner.name,
              }),
            );
          }
        }
      }

      // Sync title changes to current user's myMilestoneMaps and accessedMilestoneMaps
      if (milestoneMap && 'title' in updates) {
        const newTitle = updates.title as string;
        const currentUserProfile = state.map3Data.userProfile;
        const userProfileUpdates: Partial<UserProfile1> = {};

        // Update myMilestoneMaps if the map exists there
        const myMaps = currentUserProfile.myMilestoneMaps || [];
        const myMapIndex = myMaps.findIndex((m) => m.uuid === uuid);
        if (myMapIndex !== -1) {
          userProfileUpdates.myMilestoneMaps = myMaps.map((m) =>
            m.uuid === uuid ? { ...m, title: newTitle } : m,
          );
        }

        // Update accessedMilestoneMaps if the map exists there
        const accessedMaps = currentUserProfile.accessedMilestoneMaps || [];
        const accessedMapIndex = accessedMaps.findIndex((m) => m.uuid === uuid);
        if (accessedMapIndex !== -1) {
          userProfileUpdates.accessedMilestoneMaps = accessedMaps.map((m) =>
            m.uuid === uuid ? { ...m, title: newTitle } : m,
          );
        }

        // Update sharedMilestoneMaps if the map exists there (current user is a collaborator)
        const sharedMaps = currentUserProfile.sharedMilestoneMaps || [];
        const sharedMapIndex = sharedMaps.findIndex((m) => m.uuid === uuid);
        if (sharedMapIndex !== -1) {
          userProfileUpdates.sharedMilestoneMaps = sharedMaps.map((m) =>
            m.uuid === uuid ? { ...m, title: newTitle } : m,
          );
        }

        // Dispatch single user_update if any arrays were updated
        if (Object.keys(userProfileUpdates).length > 0) {
          dispatchChanges(dispatch, 'user_update', {
            uuid: currentUserProfile.uid,
            updates: userProfileUpdates,
          });
        }
      }

      dispatchChanges(dispatch, 'map_update', { uuid, updates: sanitizedUpdates });
      return { uuid, updates: sanitizedUpdates };
    } catch (error) {
      console.error('Error updating milestone map:', error);
      throw error;
    }
  },
);

export const createMilestoneMap = createAsyncThunk<
  MilestoneMap1,
  MilestoneMap1,
  { dispatch: AppDispatch; state: RootState }
>('map3Pending/createMilestoneMap', async (newMap: MilestoneMap1, { dispatch, getState }) => {
  try {
    dispatchChanges(dispatch, 'map_create', {
      uuid: newMap.uuid,
      updates: newMap,
    });

    // Get current user state
    const state = getState();
    const currentUserProfile = state.map3Data.userProfile;

    // Only update myMilestoneMaps if the current user is the owner
    if (currentUserProfile.userName === newMap.owner.name) {
      const existingMaps = currentUserProfile.myMilestoneMaps || [];
      const newMapEntry = {
        uuid: newMap.uuid,
        shortId: newMap.shortId,
        title: newMap.title || '',
        ownerName: newMap.owner.name,
        lastModified: new Date().toISOString(),
      };

      // Update user's myMilestoneMaps using upsertItem
      const updatedMaps = upsertItem(existingMaps, newMapEntry, (map) => map.uuid === newMap.uuid);

      // Queue the user profile update operation
      dispatchChanges(dispatch, 'user_update', {
        uuid: currentUserProfile.uid,
        updates: {
          myMilestoneMaps: updatedMaps,
        } as Partial<UserProfile1>,
      });
    }

    return newMap;
  } catch (error) {
    console.error('Failed to create milestone map:', error);
    throw error;
  }
});

export const createObjective = createAsyncThunk<Objective1, Objective1, { dispatch: AppDispatch }>(
  'map3Pending/createObjective',
  async (newObjective: Objective1, { dispatch }) => {
    try {
      dispatchChanges(dispatch, 'objective_create', {
        uuid: newObjective.uuid,
        updates: newObjective,
      });

      return newObjective;
    } catch (error) {
      console.error('Error queuing objective create:', error);
      throw error;
    }
  },
);

export const updateSignUpLink = createAsyncThunk<
  void,
  { signUpCode: string; updates: Partial<SignUpLink1> },
  { dispatch: AppDispatch }
>('map3Pending/updateSignUpLink', async ({ signUpCode, updates }, { dispatch }) => {
  try {
    dispatchChanges(dispatch, 'signup_link_update', {
      uuid: signUpCode,
      updates,
    });
  } catch (error) {
    console.error('Error updating signup link:', error);
    throw error;
  }
});

// --- Human CUD thunks (Create, Update, Delete) ---
// Reads come from map3DataSlice via Firestore listeners, not from pendingSlice.

export const createHuman = createAsyncThunk<
  Human,
  Human,
  { dispatch: AppDispatch }
>('map3Pending/createHuman', async (newHuman: Human, { dispatch }) => {
  try {
    dispatchChanges(dispatch, 'human_create', {
      uuid: newHuman.uuid,
      updates: newHuman,
    });
    return newHuman;
  } catch (error) {
    console.error('Error queuing human create:', error);
    throw error;
  }
});

/**
 * Ensures a Human document exists for the current authenticated user.
 *
 * Follows the Redux thunk pattern (like fetchMilestoneMap / fetchObjectives):
 * 1. Checks Redux state for an existing Human with matching uid
 * 2. If not in state, queries Firestore directly (state is empty on page refresh)
 * 3. If found in Firestore, loads into Redux via handleChange
 * 4. If not found anywhere, creates a new Human via createHuman thunk
 *
 * Returns the Human (existing or newly created).
 */
export const ensureCurrentUserHuman = createAsyncThunk<
  Human,
  void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>('map3Pending/ensureCurrentUserHuman', async (_, { dispatch, getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userProfile = state.map3Data.userProfile;

    if (!userProfile?.uid) {
      return rejectWithValue('No authenticated user profile');
    }

    // 1. Check Redux state first
    const existingInState = state.map3Data.humans.find((h) => h.uid === userProfile.uid);
    if (existingInState) {
      return existingInState;
    }

    // 2. Query Firestore — Redux state starts empty on page refresh
    const q = query(
      collection(db, 'humans'),
      where('uid', '==', userProfile.uid),
      limit(1),
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      // Found in Firestore — load into Redux state
      const existingHuman = snapshot.docs[0].data() as Human;
      dispatch(
        handleChange({
          operationId: `load-existing-human-${existingHuman.uuid}`,
          type: 'human_create',
          data: { uuid: existingHuman.uuid, updates: existingHuman },
          source: 'firestore',
        }),
      );
      return existingHuman;
    }

    // 3. Not found anywhere — create a new Human
    const newHuman = buildHumanFromUserProfile(userProfile);
    await dispatch(createHuman(newHuman));
    return newHuman;
  } catch (error) {
    console.error('Error ensuring current user Human:', error);
    return rejectWithValue(
      error instanceof Error ? error.message : 'Error ensuring current user Human',
    );
  }
});

export const updateHuman = createAsyncThunk<
  UpdateData<Human>,
  UpdateData<Human>,
  { dispatch: AppDispatch }
>('map3Pending/updateHuman', async (payload: UpdateData<Human>, { dispatch }) => {
  try {
    const { uuid, updates } = payload;
    dispatchChanges(dispatch, 'human_update', { uuid, updates });
    return payload;
  } catch (error) {
    console.error('Error updating human:', error);
    throw error;
  }
});

/**
 * Deletes a Human and performs a cascade via Firestore writeBatch:
 * - Deletes the Human document from 'humans' collection
 * - Deletes all HumanRelationships where humanUuid matches the deleted Human's uuid
 * - Removes the Human's uuid from any Objective1.humans[] arrays (via objective_update)
 *
 * Optimistic updates are applied before the Firestore write:
 * - A single human_delete handleChange cascades all state removals in map3DataSlice
 * - Each affected Objective1.humans[] array is updated via objective_update (queued)
 */
export const deleteHuman = createAsyncThunk<
  string, // Return the uuid of the deleted human
  Human, // Accept a Human object
  { dispatch: AppDispatch; state: RootState }
>('map3Pending/deleteHuman', async (human: Human, { dispatch, getState }) => {
  try {
    const state = getState();
    const humanUuid = human.uuid;

    // Find related HumanRelationships for the Firestore batch delete
    const relatedRelationships = state.map3Data.humanRelationships.filter(
      (r) => r.humanUuid === humanUuid,
    );

    // Find objectives that reference this human uuid for cleanup
    const affectedObjectives = state.map3Data.objectives.filter(
      (obj) => obj.humans?.includes(humanUuid),
    );

    // Apply single optimistic update: human_delete cascades in map3DataSlice to also
    // remove all related HumanRelationships and clean up Objective1.humans[] arrays
    dispatch(
      handleChange({
        operationId: crypto.randomUUID(),
        type: 'human_delete',
        data: { uuid: humanUuid, updates: {} },
        source: 'map3PendingSlice',
      }),
    );

    // Queue objective_update operations to persist Objective1.humans[] cleanup to Firestore
    for (const obj of affectedObjectives) {
      dispatchChanges(dispatch, 'objective_update', {
        uuid: obj.uuid,
        updates: { humans: (obj.humans || []).filter((id) => id !== humanUuid) } as Partial<Objective1>,
      });
    }

    // Perform atomic delete using Firestore writeBatch
    const batch = writeBatch(db);

    // Delete the Human document
    const humanRef = doc(db, 'humans', humanUuid);
    batch.delete(humanRef);

    // Delete all related HumanRelationship documents
    for (const rel of relatedRelationships) {
      const relRef = doc(db, 'humanRelationships', rel.uuid);
      batch.delete(relRef);
    }

    await batch.commit();

    return humanUuid;
  } catch (error) {
    console.error('Error deleting human with cascade:', error);
    throw error;
  }
});

// --- HumanRelationship CUD thunks (Create, Update, Delete) ---
// Reads come from map3DataSlice via Firestore listeners, not from pendingSlice.

/**
 * Creates a HumanRelationship with client-side overlapping date validation.
 * Uses dispatchChanges for optimistic update + queue.
 *
 * If the new relationship's date range overlaps with an existing relationship
 * for the same humanUuid + targetUuid, the write is rejected with an error
 * (no Firestore write occurs).
 */
export const createHumanRelationship = createAsyncThunk<
  HumanRelationship,
  HumanRelationship,
  { dispatch: AppDispatch; state: RootState }
>('map3Pending/createHumanRelationship', async (newRel: HumanRelationship, { dispatch, getState, rejectWithValue }) => {
  try {
    // Client-side overlapping date validation
    const state = getState();
    const existingRelationships = state.map3Data.humanRelationships;

    if (hasOverlappingDateRange(existingRelationships, newRel)) {
      return rejectWithValue(
        'Cannot create relationship: date range overlaps with an existing relationship for the same human and target.',
      ) as any;
    }

    dispatchChanges(dispatch, 'human_relationship_create', {
      uuid: newRel.uuid,
      updates: newRel,
    });
    return newRel;
  } catch (error) {
    console.error('Error queuing human relationship create:', error);
    throw error;
  }
});

/**
 * Updates a HumanRelationship with client-side overlapping date validation.
 * If date fields are being updated, validates against existing relationships
 * (excluding the relationship being updated itself).
 */
export const updateHumanRelationship = createAsyncThunk<
  UpdateData<HumanRelationship>,
  UpdateData<HumanRelationship>,
  { dispatch: AppDispatch; state: RootState }
>('map3Pending/updateHumanRelationship', async (payload: UpdateData<HumanRelationship>, { dispatch, getState, rejectWithValue }) => {
  try {
    const { uuid, updates } = payload;
    const state = getState();
    const existingRelationships = state.map3Data.humanRelationships;
    const currentRel = existingRelationships.find((r) => r.uuid === uuid);

    // Client-side overlapping date validation when date fields are updated
    if ('startDate' in updates || 'endDate' in updates) {
      if (currentRel) {
        // Build the merged relationship for validation
        const mergedRel = { ...currentRel, ...updates };
        // Exclude the current relationship from the overlap check
        const othersOnly = existingRelationships.filter((r) => r.uuid !== uuid);

        if (hasOverlappingDateRange(othersOnly, mergedRel)) {
          return rejectWithValue(
            'Cannot update relationship: date range overlaps with an existing relationship for the same human and target.',
          ) as any;
        }
      }
    }

    // Merge full relationship so processOperationQueue can compute the deterministic doc ID
    const mergedUpdates = currentRel ? { ...currentRel, ...updates } : updates;
    dispatchChanges(dispatch, 'human_relationship_update', { uuid, updates: mergedUpdates });
    return payload;
  } catch (error) {
    console.error('Error updating human relationship:', error);
    throw error;
  }
});

/**
 * Deletes a HumanRelationship.
 *
 * Accepts the full HumanRelationship so that processOperationQueue can derive
 * the deterministic Firestore document ID via generateRelationshipDocId.
 * (The Firestore doc ID is NOT the same as rel.uuid — it's
 *  `${relationshipType}__${humanUuid}__${targetUuid}`.)
 */
export const deleteHumanRelationship = createAsyncThunk<
  string,
  HumanRelationship,
  { dispatch: AppDispatch }
>('map3Pending/deleteHumanRelationship', async (relationship: HumanRelationship, { dispatch }) => {
  try {
    dispatchChanges(dispatch, 'human_relationship_delete', {
      uuid: relationship.uuid,
      updates: relationship, // Store full relationship so queue processor can compute doc ID
    });
    return relationship.uuid;
  } catch (error) {
    console.error('Error queuing human relationship delete:', error);
    throw error;
  }
});

/**
 * Creates a Human and an initial HumanRelationship atomically using Firestore writeBatch.
 * Both the human document and the relationship document are written in a single batch.
 * Optimistic updates are applied via dispatchChanges for both entities.
 *
 * This follows the dual-write pattern: use writeBatch for atomicity when writing
 * entity + relationship docs together.
 */
export const createHumanWithRelationship = createAsyncThunk<
  { human: Human; relationship: HumanRelationship },
  { human: Human; relationship: HumanRelationship },
  { dispatch: AppDispatch; state: RootState }
>('map3Pending/createHumanWithRelationship', async ({ human, relationship }, { dispatch, getState, rejectWithValue }) => {
  try {
    // Client-side overlapping date validation
    const state = getState();
    const existingRelationships = state.map3Data.humanRelationships;

    if (hasOverlappingDateRange(existingRelationships, relationship)) {
      return rejectWithValue(
        'Cannot create relationship: date range overlaps with an existing relationship for the same human and target.',
      ) as any;
    }

    // Apply optimistic updates for both entities
    dispatchChanges(dispatch, 'human_create', {
      uuid: human.uuid,
      updates: human,
    });
    dispatchChanges(dispatch, 'human_relationship_create', {
      uuid: relationship.uuid,
      updates: relationship,
    });

    // Perform atomic write using Firestore writeBatch.
    // Strip undefined fields — Firestore rejects explicit `undefined` values.
    const batch = writeBatch(db);
    const humanRef = doc(db, 'humans', human.uuid);
    batch.set(humanRef, stripUndefinedFields(human as unknown as Record<string, unknown>));

    const relDocId = generateRelationshipDocId(
      relationship.relationshipType,
      relationship.humanUuid,
      relationship.targetUuid,
    );
    const relRef = doc(db, 'humanRelationships', relDocId);
    batch.set(relRef, stripUndefinedFields(relationship as unknown as Record<string, unknown>));

    await batch.commit();

    return { human, relationship };
  } catch (error) {
    console.error('Error creating human with relationship:', error);
    throw error;
  }
});

export const createUserProfile = createAsyncThunk<
  void,
  { userData: Partial<UserProfile1> },
  { dispatch: AppDispatch }
>('map3Pending/createUserProfile', async ({ userData }, { dispatch }) => {
  try {
    dispatchChanges(dispatch, 'user_create', {
      uuid: userData.uid!,
      updates: userData,
    });
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
});

// Add new thunk for processing the queue
export const processOperationQueue = createAsyncThunk(
  'map3Pending/processQueue',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const { operations, syncStatus } = state.map3Pending;

    // Don't process if already syncing
    if (syncStatus === 'syncing') {
      return;
    }

    dispatch(setSyncStatus('syncing'));

    try {
      // Get pending operations, sorted by timestamp
      const pendingOps = operations
        .filter(
          (op: PendingOperation) =>
            op.status === 'pending' || (op.status === 'error' && op.retryCount < MAX_RETRIES),
        )
        .sort((a: PendingOperation, b: PendingOperation) => a.timestamp - b.timestamp)
        .slice(0, BATCH_SIZE);

      for (const operation of pendingOps) {
        try {
          dispatch(
            updateOperationStatus({
              operationId: operation.operationId,
              status: 'processing',
              lastAttempt: Date.now(), // Add timestamp for attempt
            }),
          );

          let updateSuccessful = false;

          switch (operation.type) {
            case 'objective_create': {
              if (!isCompleteObjectiveData(operation.data)) {
                throw new Error('Create operation requires complete objective data');
              }
              const { uuid, updates } = operation.data;
              const docRef = doc(db, 'objectives', uuid);
              await setDoc(docRef, updates);
              updateSuccessful = true;
              break;
            }
            case 'objective_update': {
              const { uuid, updates } = operation.data;
              const docRef = doc(db, 'objectives', uuid);
              await updateDoc(docRef, updates);
              updateSuccessful = true;
              break;
            }
            case 'map_create': {
              if (!isCompleteMilestoneMapData(operation.data)) {
                throw new Error('Create operation requires complete map data');
              }
              const { uuid, updates } = operation.data;
              const docRef = doc(db, 'milestoneMaps', uuid);
              await setDoc(docRef, updates);
              updateSuccessful = true;
              break;
            }
            case 'map_update': {
              const { uuid, updates } = operation.data;
              if (!isCompleteMilestoneMapData(operation.data)) {
                throw new Error('Invalid milestone map data');
              }
              const docRef = doc(db, 'milestoneMaps', uuid);
              await updateDoc(docRef, updates);
              updateSuccessful = true;
              break;
            }
            case 'user_create': {
              if (isCompleteUserData(operation.data)) {
                const userRef = doc(db, 'userProfiles', operation.data.uuid);
                await setDoc(userRef, operation.data.updates);
              }
              updateSuccessful = true;
              break;
            }
            case 'user_update': {
              if (isCompleteUserData(operation.data)) {
                const userRef = doc(db, 'userProfiles', operation.data.uuid);
                await updateDoc(userRef, operation.data.updates);
              }
              updateSuccessful = true;
              break;
            }
            case 'objective_delete': {
              const { uuid } = operation.data;
              const docRef = doc(db, 'objectives', uuid);
              await deleteDoc(docRef);
              updateSuccessful = true;
              break;
            }
            case 'user_delete': {
              const { uuid } = operation.data;
              const auth = getAuth();

              // Check if the current user is the one being deleted
              if (auth.currentUser?.uid !== uuid) {
                throw new Error('Can only delete your own user account');
              }

              // Delete from Firestore
              const docRef = doc(db, 'userProfiles', uuid);
              await deleteDoc(docRef);

              // Delete from Firebase Auth
              if (auth.currentUser) {
                await deleteUser(auth.currentUser);
              }

              updateSuccessful = true;
              break;
            }
            case 'map_delete': {
              const { uuid } = operation.data;
              const docRef = doc(db, 'milestoneMaps', uuid);
              await deleteDoc(docRef);
              updateSuccessful = true;
              break;
            }
            case 'signup_link_update': {
              const { uuid, updates } = operation.data;
              const docRef = doc(db, 'signUpLinks', uuid);
              await updateDoc(docRef, updates);
              updateSuccessful = true;
              break;
            }
            case 'signup_link_create': {
              if (!isCompleteSignUpLinkData(operation.data)) {
                console.error('Incomplete signup link data:', operation.data);
                throw new Error('Create operation requires complete signup link data');
              }
              const { uuid, updates } = operation.data;
              console.log('Creating signup link in Firestore:', uuid, updates);
              const docRef = doc(db, 'signUpLinks', uuid);
              await setDoc(docRef, updates);
              console.log('Signup link created successfully in Firestore');
              updateSuccessful = true;
              break;
            }
            case 'human_create': {
              const { uuid, updates } = operation.data;
              const docRef = doc(db, 'humans', uuid);
              // Strip undefined fields — Firestore rejects explicit `undefined` values
              await setDoc(docRef, stripUndefinedFields(updates as Record<string, any>));
              updateSuccessful = true;
              break;
            }
            case 'human_update': {
              const { uuid, updates } = operation.data;
              const docRef = doc(db, 'humans', uuid);
              // Strip undefined fields — Firestore rejects explicit `undefined` values
              await updateDoc(docRef, stripUndefinedFields(updates as Record<string, any>));
              updateSuccessful = true;
              break;
            }
            case 'human_relationship_create': {
              const { uuid, updates } = operation.data;
              const relData = updates as HumanRelationship;
              const relDocId = generateRelationshipDocId(
                relData.relationshipType,
                relData.humanUuid,
                relData.targetUuid,
              );
              const docRef = doc(db, 'humanRelationships', relDocId);
              // Strip undefined fields — Firestore rejects explicit `undefined` values
              await setDoc(docRef, stripUndefinedFields(updates as Record<string, any>));
              updateSuccessful = true;
              break;
            }
            case 'human_relationship_update': {
              const { updates } = operation.data;
              const relData = updates as HumanRelationship;
              const relDocId = generateRelationshipDocId(
                relData.relationshipType,
                relData.humanUuid,
                relData.targetUuid,
              );
              const docRef = doc(db, 'humanRelationships', relDocId);
              // Strip undefined fields — Firestore rejects explicit `undefined` values
              await updateDoc(docRef, stripUndefinedFields(updates as Record<string, any>));
              updateSuccessful = true;
              break;
            }
            case 'human_relationship_delete': {
              const { updates } = operation.data;
              const relData = updates as HumanRelationship;
              const relDocId = generateRelationshipDocId(
                relData.relationshipType,
                relData.humanUuid,
                relData.targetUuid,
              );
              const docRef = doc(db, 'humanRelationships', relDocId);
              await deleteDoc(docRef);
              updateSuccessful = true;
              break;
            }
            case 'human_delete': {
              // human_delete is handled atomically inside the deleteHuman thunk via writeBatch.
              // It should not be reached via processOperationQueue (deleteHuman does not call dispatchChanges).
              // This case exists for TypeScript exhaustiveness and defensive safety only.
              const { uuid } = operation.data;
              const docRef = doc(db, 'humans', uuid);
              await deleteDoc(docRef);
              updateSuccessful = true;
              break;
            }
            // Add other operation types here
            //
            // TODO: Revisit.
            //
            // It seems likely we'll need more specialized handling for data in arrays
            // nested under objectives once we have more active users collaborating live and
            // updating the same objectives at once (on the same or different maps). These include:
            // - results
            // - comments
            // - status
            default: {
              console.error(`Unhandled operation type: ${operation.type}`);
              throw new Error(`Unhandled operation type: ${operation.type}`);
            }
          }

          if (updateSuccessful) {
            dispatch(
              updateOperationStatus({
                operationId: operation.operationId,
                status: 'completed',
                lastAttempt: Date.now(),
              }),
            );
          } else {
            throw new Error('Operation did not complete successfully');
          }
        } catch (error) {
          console.error(`Operation ${operation.operationId} failed:`, error);
          console.log('Operation:', operation);
          const newRetryCount = (operation.retryCount || 0) + 1;

          if (newRetryCount >= MAX_RETRIES) {
            dispatch(
              updateOperationStatus({
                operationId: operation.operationId,
                status: 'error',
                error: error instanceof Error ? error.message : 'Unknown error',
                retryCount: newRetryCount,
                lastAttempt: Date.now(), // Add timestamp for failed attempt
              }),
            );
          } else {
            // Schedule retry
            setTimeout(() => {
              dispatch(
                updateOperationStatus({
                  operationId: operation.operationId,
                  status: 'pending',
                  error: undefined,
                  retryCount: newRetryCount,
                  lastAttempt: Date.now(), // Add timestamp for retry attempt
                }),
              );
            }, RETRY_DELAY * newRetryCount);
          }
        }
      }

      dispatch(setSyncStatus('idle'));
    } catch (error) {
      console.error('Queue processing failed:', error);
      dispatch(setSyncError(error instanceof Error ? error.message : 'Unknown error'));
    }
  },
);

// Slice
export const map3PendingSlice = createSlice({
  name: 'map3Pending',
  initialState,
  reducers: {
    queueOperation: (
      state,
      action: PayloadAction<
        Omit<PendingOperation, 'status' | 'retryCount' | 'timestamp' | 'lastAttempt'>
      >,
    ) => {
      console.log('Queueing operation:', action.payload);
      state.operations.push({
        ...action.payload,
        status: 'pending',
        retryCount: 0,
        timestamp: Date.now(),
      });
    },
    updateOperationStatus: (
      state,
      action: PayloadAction<{
        operationId: string;
        status: PendingOperation['status'];
        error?: string;
        retryCount?: number;
        lastAttempt?: number;
      }>,
    ) => {
      const operation = state.operations.find(
        (op) => op.operationId === action.payload.operationId,
      );
      if (operation) {
        operation.status = action.payload.status;
        if (action.payload.error !== undefined) {
          operation.error = action.payload.error;
        }
        if (action.payload.retryCount !== undefined) {
          operation.retryCount = action.payload.retryCount;
        }
        if (action.payload.lastAttempt !== undefined) {
          operation.lastAttempt = action.payload.lastAttempt;
        }
      }
    },
    removeOperation: (state, action: PayloadAction<string>) => {
      state.operations = state.operations.filter((op) => op.operationId !== action.payload);
    },
    setSyncStatus: (state, action: PayloadAction<PendingState['syncStatus']>) => {
      state.syncStatus = action.payload;
      state.lastSyncAttempt = Date.now();
    },
    setSyncError: (state, action: PayloadAction<string>) => {
      state.lastSyncError = action.payload;
      state.syncStatus = 'error';
      state.lastSyncAttempt = Date.now();
    },
  },
});

export const {
  queueOperation,
  updateOperationStatus,
  removeOperation,
  setSyncStatus,
  setSyncError,
} = map3PendingSlice.actions;

export default map3PendingSlice.reducer;

// Type guard functions to narrow the union type
export function isCompleteObjectiveData(
  data: UpdateData<Objective1 | MilestoneMap1 | UserProfile1>,
): data is UpdateData<Objective1> {
  const updates = data.updates as Partial<Objective1>;
  return (
    typeof data.uuid === 'string' &&
    typeof updates === 'object' &&
    typeof updates.text === 'string' &&
    Array.isArray(updates.results) &&
    Array.isArray(updates.statuses) &&
    Array.isArray(updates.comments)
  );
}

function isCompleteMilestoneMapData(
  data: UpdateData<Objective1 | MilestoneMap1 | UserProfile1>,
): data is UpdateData<MilestoneMap1> {
  try {
    const complete: MilestoneMap1 = { ...data.updates } as MilestoneMap1;
    return true;
  } catch {
    throw new Error('Invalid data type for map create');
  }
}

function isCompleteUserData(
  data: UpdateData<Objective1 | MilestoneMap1 | UserProfile1>,
): data is UpdateData<UserProfile1> {
  try {
    const complete: UserProfile1 = { ...data.updates } as UserProfile1;
    return true;
  } catch {
    throw new Error('Invalid data type for user update');
  }
}

function isCompleteSignUpLinkData(
  data: UpdateData<Objective1 | MilestoneMap1 | UserProfile1 | SignUpLink1>,
): data is UpdateData<SignUpLink1> {
  try {
    const updates = data.updates as SignUpLink1;

    // Check if all required properties of SignUpLink1 are present
    return (
      typeof data.uuid === 'string' &&
      typeof updates.toEmail === 'string' &&
      typeof updates.signUpCode === 'string' &&
      typeof updates.createdOn === 'string' &&
      typeof updates.expirationDate === 'string' &&
      typeof updates.accountType === 'string' &&
      typeof updates.url === 'string' &&
      typeof updates.status === 'string' &&
      typeof updates.attempts === 'number' &&
      (updates.lastAttempt === null || typeof updates.lastAttempt === 'string') &&
      (updates.acceptedDate === null || typeof updates.acceptedDate === 'string') &&
      Array.isArray(updates.emails)
    );
  } catch (error) {
    console.error('Invalid data for signup link create:', error, data);
    return false;
  }
}

// Add the thunk
export const deleteObjective = createAsyncThunk<
  string, // Return the uuid of deleted objective
  string, // Take uuid as parameter
  { dispatch: AppDispatch }
>('map3Pending/deleteObjective', async (uuid: string, { dispatch }) => {
  try {
    dispatchChanges(dispatch, 'objective_delete', {
      uuid,
      updates: {}, // Empty updates for delete operations
    });

    return uuid;
  } catch (error) {
    console.error('Error queuing objective delete:', error);
    throw error;
  }
});

/**
 * @deprecated - This implementation has a flawed premise and should not be used.
 * It incorrectly assumes that users can delete their own profiles, but prevents admin
 * users from managing user profiles, which is a legitimate admin function.
 *
 * Admins should use the `deleteUserProfile` function from admin1Slice instead.
 *
 * @see docs/work_in_progress/users-to-userProfiles/ADR.AdminUserProfileDeletion.md for details.
 */
/*
export const deleteUserData = createAsyncThunk<
  string, // Return type: uid of deleted user
  string, // Parameter type: uid to delete
  { dispatch: AppDispatch }
>('map3Pending/deleteUserData', async (uid: string, { dispatch }) => {
  try {
    dispatchChanges(dispatch, 'user_delete', {
      uuid: uid,
      updates: {}, // Empty updates for delete operations
    });
    return uid;
  } catch (error) {
    console.error('Error queuing user delete:', error);
    throw error;
  }
});
*/

export const deleteMilestoneMap = createAsyncThunk<
  string, // Return type: uuid of deleted map
  string, // Parameter type: shortId to delete
  { dispatch: AppDispatch; state: RootState }
>('map3Pending/deleteMilestoneMap', async (shortId: string, { dispatch, getState }) => {
  try {
    const state = getState();
    const currentUserProfile = state.map3Data.userProfile;

    // Find the map in user's myMilestoneMaps
    const mapEntry = currentUserProfile.myMilestoneMaps?.find((m) => m.shortId === shortId);

    if (!mapEntry) {
      // TODO: Review/Revise.
      // As of 2/13/25, the only place in the UI where a user can delete a map is in the
      // MyMaps listing page. This will surely change in the future. And this code will need to be updated.
      // For now, until more comprehensive access controls are in place, this is probably a could
      // check to have in place so nobody accidentally deletes a map that's not theirs.
      throw new Error('Map not found in user MyMaps listing');
    }

    // Queue the map deletion from user's local cache and Firestore.
    dispatchChanges(dispatch, 'map_delete', {
      uuid: mapEntry.uuid,
      updates: {}, // Empty updates for delete operation
    });

    // Queue the user update to remove map from lists
    const updatedMaps =
      currentUserProfile.myMilestoneMaps?.filter((m) => m.shortId !== shortId) || [];
    const accessedMaps =
      currentUserProfile.accessedMilestoneMaps?.filter((m) => m.shortId !== shortId) || [];

    // Queue updates to user's myMilestoneMaps and accessedMilestoneMaps lists.
    dispatchChanges(dispatch, 'user_update', {
      uuid: currentUserProfile.uid,
      updates: {
        myMilestoneMaps: updatedMaps,
        accessedMilestoneMaps: accessedMaps,
      } as Partial<UserProfile1>,
    });

    return mapEntry.uuid;
  } catch (error) {
    console.error('Error queuing milestone map delete:', error);
    throw error;
  }
});

export const updateUser = createAsyncThunk<
  { uid: string; updates: Partial<UserProfile1> }, // Return type
  { uid: string; updates: Partial<UserProfile1> }, // Parameter type
  { dispatch: AppDispatch }
>('map3Pending/updateUser', async (payload, { dispatch }) => {
  try {
    dispatchChanges(dispatch, 'user_update', {
      uuid: payload.uid,
      updates: payload.updates,
    });
    return payload;
  } catch (error) {
    console.error('Error queuing user update:', error);
    throw error;
  }
});

// Define the share update request type
interface ShareUpdateRequest {
  requestId: string;
  type: 'add' | 'remove';
  targetUserUid: string;
  mapUuid: string;
  mapShortId: string;
  mapTitle: string;
  createdBy: {
    uid: string;
    name: string;
  };
  status: 'pending' | 'processed' | 'error';
  createdAt: string;
  processedAt?: string;
  error?: string;
}

// Add the thunk for queueing updates to users' shared maps lists
/**
 * Queue a shared maps update request.
 *
 * @see functions/src/processSharedMapsUpdates.ts
 *
 * @param payload - The payload for the shared maps update request.
 * @param getState - The getState function from the Redux store.
 * @returns void
 */
export const queueSharedMapsUpdate = createAsyncThunk<
  void,
  {
    type: 'add' | 'remove';
    targetUserUid: string;
    mapUuid: string;
    mapShortId: string;
    mapTitle: string;
    mapOwnerName: string;
  },
  { dispatch: AppDispatch; state: RootState }
>('map3Pending/queueSharedMapsUpdate', async (payload, { getState }) => {
  const state = getState();
  const currentUserProfile = state.map3Data.userProfile;

  // Create the shared maps update request
  const request: SharedMapsUpdate = {
    uuid: crypto.randomUUID(),
    ...payload,
    createdBy: {
      uid: currentUserProfile.uid,
      name: currentUserProfile.userName,
    },
    status: 'pending',
    createdAt: new Date().toISOString(),
  };

  // Add to Firestore sharedMapsUpdates collection
  const requestRef = doc(db, 'sharedMapsUpdates', request.uuid);
  await setDoc(requestRef, request);
});

/**
 * Create a new signup link
 */
export const createSignUpLink = createAsyncThunk<
  SignUpLink1,
  {
    toEmail: string;
    accountType?: 'alpha_guest' | 'alpha_member';
    expirationDays?: number;
    createdBy?: string;
  },
  { dispatch: AppDispatch }
>('map3Pending/createSignUpLink', async (payload, { dispatch }) => {
  try {
    const { toEmail, accountType = 'alpha_guest', expirationDays = 7, createdBy } = payload;

    // Generate unique code using native crypto
    const signUpCode = crypto.randomUUID();

    // Calculate dates
    const now = new Date();
    const expirationDate = new Date(now);
    expirationDate.setDate(now.getDate() + expirationDays);

    // Create signup link document
    const signUpLink: SignUpLink1 = {
      toEmail,
      signUpCode,
      createdOn: now.toISOString(),
      expirationDate: expirationDate.toISOString(),
      accountType,
      url: `${process.env.REACT_APP_BASE_URL || window.location.origin}/sign-up?signUpCode=${signUpCode}`,
      status: 'pending',
      attempts: 0,
      lastAttempt: null,
      acceptedDate: null,
      emails: [],
      ...(createdBy ? { createdBy } : {}),
    };

    console.log('Generated SignUpLink:', {
      signUpCode,
      url: signUpLink.url,
      baseUrl: process.env.REACT_APP_BASE_URL || window.location.origin,
    });

    // Queue the signup link creation
    dispatchChanges(dispatch, 'signup_link_create', {
      uuid: signUpCode,
      updates: signUpLink,
    });

    return signUpLink;
  } catch (error) {
    console.error('Error creating signup link:', error);
    throw error;
  }
});

/**
 * Adds a dependency between two results
 */
export const addResultDependency = createAsyncThunk<
  void,
  {
    sourceObjectiveUuid: string;
    sourceResultUuid: string;
    targetResultUuid: string;
    targetObjectiveUuid: string;
    targetMapUuid: string;
  },
  { dispatch: AppDispatch; state: RootState }
>('map3Pending/addResultDependency', async (payload, { dispatch, getState }) => {
  try {
    const {
      sourceObjectiveUuid,
      sourceResultUuid,
      targetResultUuid,
      targetObjectiveUuid,
      targetMapUuid,
    } = payload;

    // Get the current state
    const state = getState();
    const objective = state.map3Data.objectives.find((obj) => obj.uuid === sourceObjectiveUuid);
    if (!objective) throw new Error('Source objective not found');

    const sourceResult = objective.results.find((result) => result.uuid === sourceResultUuid);
    if (!sourceResult) throw new Error('Source result not found');

    // Check if dependency already exists
    const dependencyExists = sourceResult.dependencies?.some(
      (dep) => dep.targetResultUuid === targetResultUuid,
    );
    if (dependencyExists) {
      throw new Error('This dependency already exists');
    }

    // Create updated result with new dependency
    const updatedResult = {
      ...sourceResult,
      dependencies: [
        ...(sourceResult.dependencies || []),
        {
          targetResultUuid,
          targetObjectiveUuid,
          targetMapUuid,
        },
      ],
    };

    // Update the objective with the new result
    const updatedResults = objective.results.map((result) =>
      result.uuid === sourceResultUuid ? updatedResult : result,
    );

    // Dispatch the update
    dispatch(
      updateObjective({
        uuid: sourceObjectiveUuid,
        updates: {
          results: updatedResults as Result1[],
        },
      }),
    );
  } catch (error) {
    console.error('Error adding result dependency:', error);
    throw error;
  }
});

/**
 * Removes a dependency between two results
 */
export const removeResultDependency = createAsyncThunk<
  void,
  {
    sourceObjectiveUuid: string;
    sourceResultUuid: string;
    targetResultUuid: string;
  },
  { dispatch: AppDispatch; state: RootState }
>('map3Pending/removeResultDependency', async (payload, { dispatch, getState }) => {
  try {
    const { sourceObjectiveUuid, sourceResultUuid, targetResultUuid } = payload;

    // Get the current state
    const state = getState();
    const objective = state.map3Data.objectives.find((obj) => obj.uuid === sourceObjectiveUuid);
    if (!objective) throw new Error('Source objective not found');

    const sourceResult = objective.results.find((result) => result.uuid === sourceResultUuid);
    if (!sourceResult) throw new Error('Source result not found');

    // Remove the dependency
    const updatedResult = {
      ...sourceResult,
      dependencies:
        sourceResult.dependencies?.filter((dep) => dep.targetResultUuid !== targetResultUuid) || [],
    };

    // Update the objective with the modified result
    const updatedResults = objective.results.map((result) =>
      result.uuid === sourceResultUuid ? updatedResult : result,
    );

    // Dispatch the update
    dispatch(
      updateObjective({
        uuid: sourceObjectiveUuid,
        updates: {
          results: updatedResults,
        },
      }),
    );
  } catch (error) {
    console.error('Error removing result dependency:', error);
    throw error;
  }
});

// Helper function for comparing map arrays (works for both myMilestoneMaps and sharedMilestoneMaps)
type MilestoneMapSummary = {
  uuid: string;
  shortId: string;
  title: string;
  starred?: boolean;
  ownerName: string;
  lastAccessed?: string;
};

function milestoneMapArraysEqual(
  a: MilestoneMapSummary[] | undefined,
  b: MilestoneMapSummary[] | undefined,
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;

  // Sort both arrays by uuid for consistent comparison
  const sortedA = [...a].sort((x, y) => x.uuid.localeCompare(y.uuid));
  const sortedB = [...b].sort((x, y) => x.uuid.localeCompare(y.uuid));

  return sortedA.every((mapA, index) => {
    const mapB = sortedB[index];
    return (
      mapA.uuid === mapB.uuid &&
      mapA.shortId === mapB.shortId &&
      mapA.title === mapB.title &&
      mapA.ownerName === mapB.ownerName
    );
    // Note: Don't compare starred/lastAccessed as these are preserved from current state
  });
}

// Refresh orchestrator thunks (following REDUX.md pattern)

// Generic refresh logic that works for both myMilestoneMaps and sharedMilestoneMaps
async function refreshMilestoneMapsGeneric(
  mapType: 'myMilestoneMaps' | 'sharedMilestoneMaps',
  dispatch: AppDispatch,
  getState: () => RootState,
  rejectWithValue: (value: string) => any,
): Promise<{ updated: boolean; maps: MilestoneMapSummary[] }> {
  // 1. Fetch fresh data via appropriate data slice thunk
  const fetchThunk =
    mapType === 'myMilestoneMaps' ? fetchMyMilestoneMapsData : fetchSharedMilestoneMapsData;
  const freshMapsAction = await dispatch(fetchThunk());

  if (fetchThunk.rejected.match(freshMapsAction)) {
    return rejectWithValue(freshMapsAction.payload as string);
  }

  const freshMaps = freshMapsAction.payload;
  const state = getState();
  const currentUserProfile = state.map3Data.userProfile;
  const currentMaps = currentUserProfile[mapType] || [];

  // 2. Transform to userProfile format, preserving starred/lastAccessed
  const refreshedMaps: MilestoneMapSummary[] = freshMaps.map((mapData) => {
    const existingMap = currentMaps.find((m) => m.uuid === mapData.uuid);
    const mapEntry: any = {
      uuid: mapData.uuid,
      shortId: mapData.shortId,
      title: mapData.title,
      ownerName: mapData.owner.name,
      starred: existingMap?.starred || false,
    };

    // Only include lastAccessed if it exists (avoid undefined values in Firestore)
    if (existingMap?.lastAccessed) {
      mapEntry.lastAccessed = existingMap.lastAccessed;
    }

    return mapEntry;
  });

  // 3. Compare with current data
  const hasChanges = !milestoneMapArraysEqual(currentMaps, refreshedMaps);

  // 4. If different, queue update operation
  if (hasChanges) {
    const updates = { [mapType]: refreshedMaps } as Partial<UserProfile1>;
    dispatchChanges(dispatch, 'user_update', {
      uuid: currentUserProfile.uid,
      updates,
    });
  }

  return { updated: hasChanges, maps: refreshedMaps };
}

export const refreshMyMilestoneMaps = createAsyncThunk<
  { updated: boolean; maps: UserProfile1['myMilestoneMaps'] },
  void,
  { dispatch: AppDispatch; state: RootState }
>('map3Pending/refreshMyMilestoneMaps', async (_, { dispatch, getState, rejectWithValue }) => {
  try {
    return await refreshMilestoneMapsGeneric(
      'myMilestoneMaps',
      dispatch,
      getState,
      rejectWithValue,
    );
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Error refreshing my milestone maps',
    );
  }
});

export const refreshSharedMilestoneMaps = createAsyncThunk<
  { updated: boolean; maps: UserProfile1['sharedMilestoneMaps'] },
  void,
  { dispatch: AppDispatch; state: RootState }
>('map3Pending/refreshSharedMilestoneMaps', async (_, { dispatch, getState, rejectWithValue }) => {
  try {
    return await refreshMilestoneMapsGeneric(
      'sharedMilestoneMaps',
      dispatch,
      getState,
      rejectWithValue,
    );
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Error refreshing shared milestone maps',
    );
  }
});
